import React from 'react';
import styled from 'styled-components';

interface ButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'outline' | 'danger';
  size?: 'small' | 'medium' | 'large';
  type?: 'button' | 'submit' | 'reset';
  className?: string;
}

const StyledButton = styled.button<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};
  font-weight: 600;
  text-decoration: none;
  border-radius: ${props => props.theme.borderRadius.md};
  transition: all 0.3s ease;
  cursor: pointer;
  
  /* Size variants */
  ${props => {
    switch (props.size) {
      case 'small':
        return `
          padding: ${props.theme.spacing.sm} ${props.theme.spacing.md};
          font-size: 0.9rem;
        `;
      case 'large':
        return `
          padding: ${props.theme.spacing.md} ${props.theme.spacing.xl};
          font-size: 1.1rem;
        `;
      default:
        return `
          padding: ${props.theme.spacing.sm} ${props.theme.spacing.lg};
          font-size: 1rem;
        `;
    }
  }}
  
  /* Color variants */
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, ${props.theme.colors.blue}, ${props.theme.colors.darkBlue});
          color: ${props.theme.colors.white};
          border: none;
          
          &:hover:not(:disabled) {
            background: linear-gradient(135deg, ${props.theme.colors.darkBlue}, ${props.theme.colors.blue});
            transform: translateY(-2px);
            box-shadow: ${props.theme.shadows.lg};
          }
        `;
      case 'secondary':
        return `
          background: ${props.theme.colors.green};
          color: ${props.theme.colors.white};
          border: none;
          
          &:hover:not(:disabled) {
            background: ${props.theme.colors.darkBlue};
            transform: translateY(-2px);
            box-shadow: ${props.theme.shadows.lg};
          }
        `;
      case 'outline':
        return `
          background: transparent;
          color: ${props.theme.colors.blue};
          border: 2px solid ${props.theme.colors.blue};
          
          &:hover:not(:disabled) {
            background: ${props.theme.colors.blue};
            color: ${props.theme.colors.white};
            transform: translateY(-2px);
          }
        `;
      case 'danger':
        return `
          background: ${props.theme.colors.red};
          color: ${props.theme.colors.white};
          border: none;
          
          &:hover:not(:disabled) {
            background: ${props.theme.colors.darkBlue};
            transform: translateY(-2px);
            box-shadow: ${props.theme.shadows.lg};
          }
        `;
      default:
        return `
          background: ${props.theme.colors.gray};
          color: ${props.theme.colors.darkGray};
          border: none;
          
          &:hover:not(:disabled) {
            background: ${props.theme.colors.darkGray};
            color: ${props.theme.colors.white};
            transform: translateY(-2px);
          }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
  }
  
  &:focus {
    outline: 3px solid ${props => props.theme.colors.blue};
    outline-offset: 2px;
  }
  
  /* WCAG compliance */
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none !important;
  }
`;

const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  disabled = false,
  variant = 'primary',
  size = 'medium',
  type = 'button',
  className
}) => {
  return (
    <StyledButton
      onClick={onClick}
      disabled={disabled}
      variant={variant}
      size={size}
      type={type}
      className={className}
    >
      {children}
    </StyledButton>
  );
};

export default Button;
